import IRunDetails from 'types/IRunDetails';

export const PAGE_SIZE = 50;

/**
 * State priority for sorting: most interesting states first.
 */
const STATE_PRIORITY: Record<string, number> = {
  FAILED: 0,
  RUNNING: 1,
  READY: 2,
  WAITING: 3,
  PAUSED: 4,
  CANCELLED: 5,
  RESERVED: 6,
  COMPLETED: 7,
  ARCHIVED: 8,
};

function statePriority(state: string): number {
  return STATE_PRIORITY[state] ?? 99;
}

/**
 * Pie chart data fields for Cytoscape pie-background.
 * Each key maps to a fixed pie slice index in the stylesheet.
 */
export interface PieFields {
  pie_failed: number;
  pie_running: number;
  pie_ready: number;
  pie_waiting: number;
  pie_paused: number;
  pie_cancelled: number;
  pie_reserved: number;
  pie_completed: number;
  pie_archived: number;
}

const STATE_TO_PIE_KEY: Record<string, keyof PieFields> = {
  FAILED: 'pie_failed',
  RUNNING: 'pie_running',
  READY: 'pie_ready',
  WAITING: 'pie_waiting',
  PAUSED: 'pie_paused',
  CANCELLED: 'pie_cancelled',
  RESERVED: 'pie_reserved',
  COMPLETED: 'pie_completed',
  ARCHIVED: 'pie_archived',
  // Transient: still occupying its worker slot while being stopped, so it
  // counts toward the running slice rather than getting one of its own.
  EVICTING: 'pie_running',
};

/**
 * Convert state counts into percentage fields for Cytoscape pie chart background.
 */
function computePieData(stateCounts: Map<string, number>, total: number): PieFields {
  const pie: PieFields = {
    pie_failed: 0,
    pie_running: 0,
    pie_ready: 0,
    pie_waiting: 0,
    pie_paused: 0,
    pie_cancelled: 0,
    pie_reserved: 0,
    pie_completed: 0,
    pie_archived: 0,
  };
  if (total === 0) return pie;
  for (const [state, count] of stateCounts) {
    const key = STATE_TO_PIE_KEY[state];
    if (key) {
      // Accumulate: multiple states can map to the same slice (e.g. RUNNING and
      // EVICTING both feed pie_running), so add rather than overwrite.
      pie[key] += (count / total) * 100;
    }
  }
  return pie;
}

/**
 * Build sorted breakdown strings like "200 completed, 30 running".
 */
function buildBreakdownParts(stateCounts: Map<string, number>): string[] {
  const sorted = [...stateCounts.entries()].sort(([a], [b]) => statePriority(a) - statePriority(b));
  return sorted.map(([state, count]) => `${count} ${state.toLowerCase()}`);
}

/**
 * For a set of child IDs, traverse each child + its exclusive subtree
 * (stopping at convergence nodes) and collect all traversed task IDs.
 */
function collectSubtreeIds(
  childIds: string[],
  taskLinks: Record<string, string[]>,
  convergence: Set<string>,
): string[] {
  const ids: string[] = [];
  for (const childId of childIds) {
    if (!convergence.has(childId)) {
      ids.push(childId);
    }
    const subtree = getDescendantsUntil(childId, taskLinks, convergence);
    for (const d of subtree) {
      ids.push(d);
    }
  }
  return ids;
}

/**
 * Find nodes that have more than `threshold` direct children in task_links.
 */
export function detectFanOutNodes(runDetails: IRunDetails, threshold = 10): Set<string> {
  const fanOutNodes = new Set<string>();
  for (const [source, targets] of Object.entries(runDetails.task_links)) {
    if (targets.length > threshold) {
      fanOutNodes.add(source);
    }
  }
  return fanOutNodes;
}

/**
 * BFS to collect all descendant IDs of a node.
 */
export function getDescendants(nodeId: string, taskLinks: Record<string, string[]>): Set<string> {
  const descendants = new Set<string>();
  const queue = [...(taskLinks[nodeId] || [])];
  while (queue.length > 0) {
    const current = queue.pop()!;
    if (descendants.has(current)) continue;
    descendants.add(current);
    for (const child of taskLinks[current] || []) {
      queue.push(child);
    }
  }
  return descendants;
}

/**
 * Like getDescendants, but does not traverse into nodes in the stopAt set.
 * Nodes in stopAt are excluded from the result.
 */
export function getDescendantsUntil(
  nodeId: string,
  taskLinks: Record<string, string[]>,
  stopAt: Set<string>,
): Set<string> {
  const descendants = new Set<string>();
  const queue = [...(taskLinks[nodeId] || [])];
  while (queue.length > 0) {
    const current = queue.pop()!;
    if (descendants.has(current) || stopAt.has(current)) continue;
    descendants.add(current);
    for (const child of taskLinks[current] || []) {
      queue.push(child);
    }
  }
  return descendants;
}

/**
 * Find convergence (fan-in) nodes for a given fan-out node.
 *
 * A convergence node is a descendant reachable from 2+ different direct
 * children (branches) of the fan-out. These nodes — and everything
 * downstream of them — should NOT be collapsed with the fan-out.
 *
 * Uses topological-order branch propagation: each direct child starts
 * with its own branch label; descendants inherit from parents; if a node
 * gets labels from 2+ branches it is marked as convergence (null). This
 * is O(V + E) over the descendant subgraph.
 */
export function findConvergenceNodes(fanOutId: string, taskLinks: Record<string, string[]>): Set<string> {
  const directChildren = taskLinks[fanOutId] || [];
  if (directChildren.length <= 1) return new Set();

  const allDescendants = getDescendants(fanOutId, taskLinks);
  const directChildSet = new Set(directChildren);

  // Build in-degree and reverse edges within the descendant subgraph
  const inDegree = new Map<string, number>();
  const reverseEdges = new Map<string, string[]>();
  for (const d of allDescendants) {
    inDegree.set(d, 0);
    reverseEdges.set(d, []);
  }
  for (const d of allDescendants) {
    for (const child of taskLinks[d] || []) {
      if (allDescendants.has(child)) {
        inDegree.set(child, inDegree.get(child)! + 1);
        reverseEdges.get(child)!.push(d);
      }
    }
  }

  // Topological sort (Kahn's algorithm)
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }
  let qi = 0;
  while (qi < queue.length) {
    const node = queue[qi++];
    for (const child of taskLinks[node] || []) {
      if (!inDegree.has(child)) continue;
      const newDeg = inDegree.get(child)! - 1;
      inDegree.set(child, newDeg);
      if (newDeg === 0) queue.push(child);
    }
  }
  const topoOrder = queue; // queue is now the full topological ordering

  // Branch propagation:
  //   string  → belongs to exactly one branch (the direct child's ID)
  //   null    → convergence (reachable from 2+ branches, or downstream of one)
  const branchOf = new Map<string, string | null>();

  for (const node of topoOrder) {
    // Collect branch info from parents within the subgraph
    const parents = reverseEdges.get(node)!;
    let branch: string | null | undefined;

    for (const p of parents) {
      const pb = branchOf.get(p);
      if (pb === null) {
        branch = null;
        break;
      }
      if (pb === undefined) continue;
      if (branch === undefined) {
        branch = pb;
      } else if (branch !== pb) {
        branch = null;
        break;
      }
    }

    // If this node is a direct child of the fan-out, it introduces its own branch
    if (directChildSet.has(node)) {
      const ownBranch = node;
      if (branch === undefined) {
        branch = ownBranch;
      } else if (branch !== null && branch !== ownBranch) {
        branch = null;
      }
    }

    branchOf.set(node, branch ?? null);
  }

  const convergence = new Set<string>();
  for (const [id, b] of branchOf) {
    if (b === null) convergence.add(id);
  }
  return convergence;
}

/**
 * Summary stats for a set of task IDs: pie data (for cytoscape background),
 * breakdown string (for tooltip), and worker-lost count.
 *
 * Used by `buildCollapsedGraph` on the initial render and by `TaskGraph`'s
 * progress-polling effect to keep collapsed-summary and pagination nodes'
 * pie charts in sync with the latest task states without a cytoscape rebuild.
 */
export function computeRegionSummary(
  taskIds: Iterable<string>,
  taskStates: Record<string, string>,
  workerLostSet?: Set<string>,
): { pieData: PieFields; breakdown: string; lostCount: number; total: number } {
  const stateCounts = new Map<string, number>();
  let total = 0;
  let lostCount = 0;
  for (const id of taskIds) {
    const state = taskStates[id] || 'UNKNOWN';
    stateCounts.set(state, (stateCounts.get(state) || 0) + 1);
    total++;
    if (workerLostSet?.has(id)) lostCount++;
  }
  const pieData = computePieData(stateCounts, total);
  const breakdown = buildBreakdownParts(stateCounts).join(', ');
  return { pieData, breakdown, lostCount, total };
}

interface SummaryNodeData extends PieFields {
  id: string;
  name: string;
  breakdown: string;
  width: number;
  isSummary: true;
  fanOutParentId: string;
  regionIds: string[];
  workerLost?: boolean;
  workerLostCount?: number;
}

interface PaginationNodeData extends PieFields {
  id: string;
  name: string;
  breakdown: string;
  pageStep: number;
  width: number;
  isPagination: true;
  fanOutParentId: string;
  paginationDirection: 'prev' | 'next';
  regionIds: string[];
  workerLost?: boolean;
  workerLostCount?: number;
}

interface GraphElement {
  data: {
    id?: string;
    name?: string;
    breakdown?: string;
    pageStep?: number;
    width?: number;
    source?: string;
    target?: string;
    taskType?: string;
    taskState?: string;
    stateClass?: string;
    workerLost?: boolean;
    workerLostCount?: number;
    isSummary?: boolean;
    isPagination?: boolean;
    paginationDirection?: 'prev' | 'next';
    fanOutParentId?: string;
    regionIds?: string[];
  } & Partial<PieFields>;
  classes?: string[];
}

/**
 * Build a collapsed graph based on the current set of collapsed/expanded fan-out nodes.
 *
 * For collapsed fan-outs: hide only the fan-out region (stopping at convergence /
 * fan-in nodes), replace with a single summary node. Downstream tasks remain visible.
 *
 * For expanded fan-outs with more children than pageSize: show a window of children
 * starting at the given offset, with "prev" / "next" pagination nodes.
 * Convergence nodes are never hidden.
 */
export function buildCollapsedGraph(
  runDetails: IRunDetails,
  collapsedNodeIds: Set<string>,
  fanOutOffsets: Map<string, number>,
  pageSize = PAGE_SIZE,
): GraphElement[] {
  const { tasks, task_links, task_states } = runDetails;
  const workerLostSet = new Set(runDetails.worker_lost_task_ids || []);

  // Collect all node IDs that should be hidden
  const hiddenIds = new Set<string>();
  // Cache fan-out regions for summary labels
  const fanOutRegions = new Map<string, Set<string>>();
  // Track paginated fan-outs with hidden child arrays for state computation
  const pagedFanOuts = new Map<string, { prevChildren: string[]; nextChildren: string[]; convergence: Set<string> }>();

  // For each collapsed fan-out, hide only the fan-out region (stop at convergence)
  for (const parentId of collapsedNodeIds) {
    const convergence = findConvergenceNodes(parentId, task_links);
    const region = getDescendantsUntil(parentId, task_links, convergence);
    fanOutRegions.set(parentId, region);
    for (const d of region) {
      hiddenIds.add(d);
    }
  }

  // For expanded fan-outs that exceed page size, apply pagination
  const allFanOuts = detectFanOutNodes(runDetails, 10);
  for (const parentId of allFanOuts) {
    if (collapsedNodeIds.has(parentId)) continue;

    const directChildren = task_links[parentId] || [];
    if (directChildren.length <= pageSize) continue;

    const convergence = findConvergenceNodes(parentId, task_links);
    const offset = fanOutOffsets.get(parentId) || 0;
    const end = Math.min(offset + pageSize, directChildren.length);

    // Children outside the visible window (before and after)
    const hiddenChildren = [...directChildren.slice(0, offset), ...directChildren.slice(end)];

    // Hide out-of-window children and their exclusive sub-trees
    for (const childId of hiddenChildren) {
      if (!convergence.has(childId)) {
        hiddenIds.add(childId);
      }
      const childDescendants = getDescendantsUntil(childId, task_links, convergence);
      for (const d of childDescendants) {
        hiddenIds.add(d);
      }
    }

    pagedFanOuts.set(parentId, {
      prevChildren: directChildren.slice(0, offset),
      nextChildren: directChildren.slice(end),
      convergence,
    });
  }

  // Build nodes: filter out hidden, add summary/pagination nodes
  const elements: GraphElement[] = [];

  for (const task of tasks) {
    if (hiddenIds.has(task.id)) continue;
    const stateClass = (task_states[task.id] || '').toLowerCase();
    const classes = [stateClass];
    if (workerLostSet.has(task.id)) {
      classes.push('worker-lost');
    }
    elements.push({
      data: {
        id: task.id,
        name: task.name,
        taskType: task.type,
        taskState: task_states[task.id] || '',
        stateClass,
        workerLost: workerLostSet.has(task.id),
        width: Math.max(5, 8.5 * String(task.name).length + 20),
      },
      classes,
    });
  }

  // Add summary nodes for collapsed fan-outs
  for (const parentId of collapsedNodeIds) {
    // Skip if the parent itself is hidden (nested inside another collapsed fan-out)
    if (hiddenIds.has(parentId)) continue;
    const region = fanOutRegions.get(parentId)!;
    if (region.size === 0) continue;

    const regionIds = [...region];
    const { pieData, breakdown, lostCount } = computeRegionSummary(regionIds, task_states, workerLostSet);
    const label = `${region.size} tasks`;

    const summaryId = `summary:${parentId}`;
    const summaryClasses = ['summary'];
    if (lostCount > 0) summaryClasses.push('worker-lost');
    elements.push({
      data: {
        id: summaryId,
        name: label,
        breakdown,
        width: Math.max(80, 7 * label.length + 20),
        isSummary: true,
        fanOutParentId: parentId,
        regionIds,
        ...(lostCount > 0 && { workerLost: true, workerLostCount: lostCount }),
        ...pieData,
      } as SummaryNodeData,
      classes: summaryClasses,
    });

    // Add edge from parent to summary
    elements.push({
      data: {
        source: parentId,
        target: summaryId,
      },
    });

    // Add edges from summary to frontier nodes — visible nodes just past the
    // collapsed region. Without these, fan-in nodes lose all incoming edges
    // (since their parents in the parallel region are hidden).
    const frontierTargets = new Set<string>();
    for (const regionNode of region) {
      for (const child of task_links[regionNode] || []) {
        if (!region.has(child) && !hiddenIds.has(child)) {
          frontierTargets.add(child);
        }
      }
    }
    for (const target of frontierTargets) {
      elements.push({
        data: {
          source: summaryId,
          target,
        },
      });
    }
  }

  // Add pagination nodes for paged expanded fan-outs
  for (const [parentId, { prevChildren, nextChildren, convergence }] of pagedFanOuts) {
    // Skip if the parent itself is hidden (nested inside a collapsed fan-out)
    if (hiddenIds.has(parentId)) continue;

    if (prevChildren.length > 0) {
      const regionIds = collectSubtreeIds(prevChildren, task_links, convergence);
      const { pieData, breakdown, lostCount, total } = computeRegionSummary(regionIds, task_states, workerLostSet);
      const prevId = `page-prev:${parentId}`;
      const label = `\u2039 ${total} tasks`;
      const prevClasses = ['pagination'];
      if (lostCount > 0) prevClasses.push('worker-lost');
      elements.push({
        data: {
          id: prevId,
          name: label,
          breakdown,
          pageStep: Math.min(prevChildren.length, pageSize),
          width: Math.max(80, 7 * label.length + 20),
          isPagination: true,
          paginationDirection: 'prev',
          fanOutParentId: parentId,
          regionIds,
          ...(lostCount > 0 && { workerLost: true, workerLostCount: lostCount }),
          ...pieData,
        } as PaginationNodeData,
        classes: prevClasses,
      });
      elements.push({
        data: { source: parentId, target: prevId },
      });
    }

    if (nextChildren.length > 0) {
      const regionIds = collectSubtreeIds(nextChildren, task_links, convergence);
      const { pieData, breakdown, lostCount, total } = computeRegionSummary(regionIds, task_states, workerLostSet);
      const nextId = `page-next:${parentId}`;
      const label = `${total} tasks \u203a`;
      const nextClasses = ['pagination'];
      if (lostCount > 0) nextClasses.push('worker-lost');
      elements.push({
        data: {
          id: nextId,
          name: label,
          breakdown,
          pageStep: Math.min(nextChildren.length, pageSize),
          width: Math.max(80, 7 * label.length + 20),
          isPagination: true,
          paginationDirection: 'next',
          fanOutParentId: parentId,
          regionIds,
          ...(lostCount > 0 && { workerLost: true, workerLostCount: lostCount }),
          ...pieData,
        } as PaginationNodeData,
        classes: nextClasses,
      });
      elements.push({
        data: { source: parentId, target: nextId },
      });
    }
  }

  // Build edges: only include edges where both source and target are visible
  const visibleIds = new Set(elements.filter((el) => el.data.id && !el.data.source).map((el) => el.data.id!));

  for (const [source, targets] of Object.entries(task_links)) {
    if (!visibleIds.has(source)) continue;
    for (const target of targets) {
      if (!visibleIds.has(target)) continue;
      elements.push({
        data: {
          source,
          target,
        },
      });
    }
  }

  return elements;
}
